import React, { useState, useImperativeHandle, forwardRef } from 'react';
import K2Api from 'lib/ajax/K2Api';
import { useK2Loader } from 'hooks/useK2Loader';
import Loader from 'components/Loader';
import ReactSelect from 'react-select';
import { Select, SelectOption, ToastService } from '@launchmetrics/lm-ui';
import Form from 'react-bootstrap/Form';
import CelebritySelect from 'components/LM/Voice/CelebritySelect';
const k2Api = new K2Api();
const MANDATORY_FORM_FIELDS = ['name', 'type'];

function DetailsForm(props, ref) {
	const { voice, allowCreateVoice, showToast = true } = props;
	const { onChange, onLoading, onVoiceCreated } = props;
	const voiceBase = {
		name: voice.name,
		type: voice.type,
		country: (voice.country) ? voice.country : null,
		labels: voice.labels,
		celebrity: voice.celebrity || null,
	}

	const [originalVoice, setOriginalVoice] = useState({ ...voiceBase });
	const [workingVoice, setWorkingVoice] = useState({ ...voiceBase });
	const [errors, setErrors] = useState(null);

	const [loadingLabels, labelsResponse] = useK2Loader('/labels');
	const labels = groupLabelsByAllowedTypes(labelsResponse);

	useImperativeHandle(
		ref, () => ({
			async onSubmit(onUpdateVoice) {

				const errors = validateFormFields();
				if (Object.keys(errors).length > 0) {
					setErrors(errors);
					onChange(true);
					onLoading(false);
					return;
				}

				if (voice.id) {
					await deleteLabels(originalVoice.labels, workingVoice.labels);
				}

				let saveVoiceResponse;
				try {
					saveVoiceResponse = await saveVoice(voice.id, workingVoice, allowCreateVoice);
				} catch (error) {
					const status = error.response.status;
					const message = error.response.data;
					if (status === 409) {
						if (message.name) {
							setErrors({ duplicates: message.name })
						}
						if (message.celebrity_id) {
							setErrors({ celebrity_id: message.celebrity_id });
						}
					} else if (status === 400) {
						if (message.country) {
							setErrors({ country: message.country });
						} else if (message?.details?.issues[0]?.params?.issue?.path[0] === 'name') {
							setErrors({ name: message?.details?.issues[0]?.message });
						} else if (message.type) {
							setErrors({ type: message.type });
						} else if (message.celebrity_id) {
							setErrors({ type: message.celebrity_id });
						}
					} else if (status === 423) {
						setErrors({ lock: 'Voice locked and cannot be modified' })
					}
					onChange(true);
					onLoading(false);
					return;
				};

				if (voice.id) {
					await saveNewLabels(voice.id, originalVoice.labels, workingVoice.labels);
					onUpdateVoice(
						['celebrity', 'influencer'].includes(workingVoice.type)
							? { ...voice, ...workingVoice }
							: { ...voice, ...workingVoice, celebrity: null }
					);
					if (showToast) {
						ToastService.show({
							id: "voice-saved",
							title: voice.status === 'candidate' ? "Voice validated successfully" : "Voice saved successfully",
							type: "success",
							duration: 3000,
						});
					}

				} else {
					await saveNewLabels(saveVoiceResponse.data.id, originalVoice.labels, workingVoice.labels);
					onVoiceCreated(saveVoiceResponse.data.id);
				}

				setOriginalVoice({ ...workingVoice });
				onChange(false)
				onLoading(false);
				setErrors(null);
			},

			onDiscard() {
				setErrors(null);
				setWorkingVoice({ ...originalVoice });
				onChange(false);
			}
		})
	);

	const validateFormFields = () => {
		const errors = {};
		for (const field of MANDATORY_FORM_FIELDS) {
			if (!workingVoice[field]) {
				errors[field] = `${field} is required`;
			}
		}

		if (voice.status === 'candidate' && !workingVoice.country) {
			errors.country = 'Country is required for validation';
		}

		return errors;
	}

	const saveNewLabels = (voiceId, originalLabels, modifiedLabels) => {

		let addedLabels;
		if (originalLabels) {
			addedLabels = modifiedLabels.filter(a => !originalLabels.some(b => a.id === b.id));
		} else {
			addedLabels = modifiedLabels;
		}

		const promises = [];
		addedLabels.forEach(addedLabel => {
			const promiseAdd = k2Api.put(`/voices/${voiceId}/labels/${addedLabel.id}`);
			promiseAdd.catch(error => {
				setErrors({ label: error.response.data });
			});
			promises.push(promiseAdd);
		});

		return Promise.allSettled(promises);
	};

	const deleteLabels = (originalLabels, modifiedLabels) => {
		const deletedLabels = originalLabels.filter(a => !modifiedLabels.some(b => a.id === b.id));

		const promises = [];
		deletedLabels.forEach(deletedLabel => {
			const promiseDelete = k2Api.delete(`/voices/${voice.id}/labels/${deletedLabel.id}`);
			promiseDelete.catch(error => {
				setErrors({ label: error.response.data });
			});
			promises.push(promiseDelete);
		});

		return Promise.allSettled(promises);
	};


	const updateWorkingVoice = (field, value) => {
		let voiceHasBeenUpdated = originalVoice[field] !== value;

		if (field === 'type' && workingVoice.labels) {
			workingVoice.labels = filterCompatibleLabels(value, workingVoice.labels, labels);
		}

		setErrors(prevState => {
			if (prevState && prevState[field]) {
				delete prevState[field];
				return { ...prevState };
			}
		})

		setWorkingVoice({ ...workingVoice, [field]: value });
		onChange(voiceHasBeenUpdated)
	}

	return <>
		<Form>
			<Form.Group className="mb-3 text-left">
				<Form.Label htmlFor="voiceName">Name:</Form.Label>
				<Form.Control name='name' type="text" size="lg" value={workingVoice.name} onChange={(ev) => updateWorkingVoice(ev.target.name, ev.target.value)} />
				{errors && <ErrorList errors={errors} type="duplicates" />}
			</Form.Group>

			<Form.Group className='mb-3 text-left'>
				<Form.Label>Type:</Form.Label>
				<TypeSelect voice={workingVoice} onChange={updateWorkingVoice} />
				{errors && <GenericError error={errors.type} />}
			</Form.Group>

			<Form.Group className="mb-3 text-left">
				<Form.Label>Country:{voice.status === 'candidate' && <span className="text-danger"> *</span>}</Form.Label>
				<CountrySelect selected={workingVoice.country} onChange={updateWorkingVoice} />
				{voice.status === 'candidate' && !workingVoice.country && !errors?.country && <p className="mb-0 text-muted pt-1"><em>Country is required for validation</em></p>}
				{errors && <GenericError error={errors.country} />}
			</Form.Group>

			{['celebrity', 'influencer'].includes(workingVoice.type) && (
				<Form.Group className="mb-3 text-left">
					<Form.Label>Celebrity Echo ID (Print documents):</Form.Label>
					<CelebritySelect selected={workingVoice.celebrity} onChange={updateWorkingVoice} />
					{errors && <ErrorList errors={errors.celebrity_id} type="celebrity_id" />}
				</Form.Group>
			)}

			<Form.Group className='mb-3 text-left'>
				<Form.Label>Labels:</Form.Label>
				{!loadingLabels && <LabelsSelect voiceType={workingVoice.type} selected={workingVoice.labels} options={labels} onChange={updateWorkingVoice} />}
				{errors && <GenericError error={errors.label} />}
			</Form.Group>
		</Form>
		{errors && <GenericError error={errors.lock} />}
	</>;
};



function filterCompatibleLabels(type, selected, labels) {
	const allowedLabelIds = new Set(labels[type].map(el => el.id));
	return selected.filter(select => allowedLabelIds.has(select.id));
}

function TypeSelect(props) {
	const { voice } = props;
	const { onChange } = props;

	const types = ['media', 'brand', 'influencer', 'celebrity'];

	const handleChange = (optionValue) => { onChange('type', optionValue.key) };

	return (
        <Select placeholder="Select..." searchable optionValue={voice.type ? { key: voice.type, value: voice.type } : null} onChange={handleChange} >
            {types.map((type) => (
                <SelectOption key={type} optionValue={{ key: type, value: type }} />
            ))}
        </Select>
    );
}

function LabelsSelect(props) {
	const { voiceType, selected, options } = props;
	const { onChange } = props;

	let selectOptions;
	if (voiceType) {
		selectOptions = options[voiceType].map(item => { return { label: item.name, value: item.id } });
	}

	let selectedValues;
	if (selected) {
		selectedValues = selected.map(item => { return { label: item.name, value: item.id } });
	}

	const handleChange = (event) => {
		const workingVoiceLabels = event.map(item => { return { id: item.value, name: item.label } });
		onChange('labels', workingVoiceLabels);
	}

	return <ReactSelect
		value={selectedValues}
		isDisabled={!voiceType}
		options={selectOptions}
		onChange={handleChange}
		isMulti={true}
		closeMenuOnSelect={false}
	/>
}

function CountrySelect(props) {
    const { selected } = props;
    const { onChange } = props;

    const [loading, countries] = useK2Loader('/countries');
    if (loading) return <Loader size={20} />;

    const handleChange = (optionValue) => {
        onChange('country', { id: optionValue.key, name: optionValue.value });
    };

    return (
        <Select placeholder="Select..." searchable optionValue={selected ? { key: selected.id, value: selected.name } : null} onChange={handleChange}>
            {countries.map((item) => (
                <SelectOption
                    key={item.id}
                    optionValue={{ key: item.id, value: item.name }}
                />
            ))}
        </Select>
    );
}


function groupLabelsByAllowedTypes(options) {
	if (!options) return;
	const dict = {};
	options.forEach(label => {
		label.allowed_types.forEach(type => {
			if (!dict[type]) {
				dict[type] = [label];
			} else {
				dict[type].push(label);
			}
		})
	});

	return dict;
}

function ErrorList({ errors, type }) {
    let body = null;
    const list = errors?.[type];
    if (Array.isArray(list)) {
        const voiceLinks = list.map(voiceId => (
            <li key={voiceId} className="list-inline-item">
                <a href={`/voices/${voiceId}`} target="_blank" rel="noopener noreferrer">{voiceId}</a>
            </li>
        ));
        const title =
            type === 'duplicates'
                ? 'Name already exist in entity:'
                : 'Celebrity has been linked to:';

        body = (
            <div className="text-warning warning-box pt-1 pb-0">
                <p className="mb-0">{title}</p>
                <ul className="list-inline">{voiceLinks}</ul>
            </div>
        );
    }
    if (errors?.name) {
        body = <GenericError error={errors.name} />;
    }
    return body;
}


function GenericError(props) {
	if (!props.error) return null;
	return <p className="mb-0 text-warning pt-1">
		<em>{props.error}</em>
	</p>
}

function saveVoice(id, properties, allowCreateVoice) {
	const payload = { 
		name: properties.name, 
		type: properties.type, 
		country_iso: properties.country?.id ?? null,
		celebrity_id: ['celebrity', 'influencer'].includes(properties.type)
			? properties.celebrity?.id ?? null
			: null,
	};

	if (id) {
		return k2Api.put(`/voices/${id}`, payload);
	} else if (!id && allowCreateVoice) {
		return k2Api.post('/voices', payload);
	} else {
		return Promise.reject('Cannot create voice because it is not enabled');
	}
}


export default forwardRef(DetailsForm);
